import { useOutletContext, Link } from "react-router-dom";
import { ArrowUpRight, Users, MessageSquare } from "lucide-react";

export default function Dashboard() {
  const { stats } = useOutletContext() || {};
  const apps = stats?.applications;
  const contacts = stats?.contacts;

  return (
    <div className="p-8 md:p-12" data-testid="admin-dashboard">
      <header className="mb-10">
        <div className="overline text-vermilion mb-3">Overview</div>
        <h1 className="font-serif text-4xl md:text-5xl leading-[1.02] tracking-tight">
          Pipeline at a glance.
        </h1>
      </header>

      {/* Top-line */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-border">
        {[
          { label: "Total applications", value: apps?.total ?? 0, to: "/admin/applications" },
          { label: "Unread applications", value: apps?.unread ?? 0, to: "/admin/applications?unread=true", accent: true },
          { label: "Total contacts", value: contacts?.total ?? 0, to: "/admin/contacts" },
          { label: "Unread contacts", value: contacts?.unread ?? 0, to: "/admin/contacts?unread=true", accent: true },
        ].map((t, i) => (
          <Link
            key={t.label}
            to={t.to}
            data-testid={`stat-${t.label.toLowerCase().replace(/ /g, "-")}`}
            className={`p-8 border-b border-border ${
              i < 3 ? "sm:border-r" : ""
            } ${
              i === 1 ? "" : ""
            } hover:bg-muted/50 transition-colors group`}
          >
            <div className="overline text-ink/50 mb-4">{t.label}</div>
            <div
              className={`display-num text-5xl md:text-6xl ${
                t.accent && t.value > 0 ? "text-vermilion" : "text-ink"
              }`}
            >
              {t.value}
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink/50 group-hover:text-vermilion transition-colors">
              Open <ArrowUpRight className="w-3.5 h-3.5" />
            </div>
          </Link>
        ))}
      </div>

      {/* Two column */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-12">
        {/* By job */}
        <section className="border-t border-border">
          <div className="py-4 flex items-center gap-2 overline text-vermilion">
            <Users className="w-3.5 h-3.5" /> Applications by role
          </div>
          <ul data-testid="stats-by-job" className="divide-y divide-border border-y border-border">
            {(apps?.by_job || []).map((j) => (
              <li key={j.job_id} className="py-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-xl tracking-tight truncate">
                    {j.title}{" "}
                    <span className="text-ink/40 text-base">· {j.level}</span>
                  </div>
                </div>
                <div className="display-num text-2xl md:text-3xl text-ink">
                  {j.count}
                </div>
                <Link
                  to={`/admin/applications?job_id=${j.job_id}`}
                  className="text-xs uppercase tracking-[0.2em] text-ink/50 hover:text-vermilion"
                >
                  View →
                </Link>
              </li>
            ))}
            {(!apps?.by_job || apps.by_job.length === 0) && (
              <li className="py-8 text-center text-ink/50">No data yet.</li>
            )}
          </ul>
        </section>

        {/* By stage + status */}
        <section className="space-y-10">
          <div className="border-t border-border">
            <div className="py-4 flex items-center gap-2 overline text-vermilion">
              <Users className="w-3.5 h-3.5" /> Applications by stage
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 border-y border-border" data-testid="stats-by-stage">
              {Object.entries(apps?.by_stage || {}).map(([k, v], i) => (
                <Link
                  key={k}
                  to={`/admin/applications?stage=${k}`}
                  className={`p-6 border-border ${i % 3 !== 2 ? "sm:border-r" : ""} ${
                    i < 3 ? "border-b sm:border-b sm:[grid-column]:span-1" : ""
                  } hover:bg-muted/50 transition-colors`}
                >
                  <div className="overline text-ink/50 mb-3 capitalize">
                    {k}
                  </div>
                  <div className="display-num text-3xl md:text-4xl">{v}</div>
                </Link>
              ))}
            </div>
          </div>

          <div className="border-t border-border">
            <div className="py-4 flex items-center gap-2 overline text-vermilion">
              <MessageSquare className="w-3.5 h-3.5" /> Contacts by status
            </div>
            <div className="grid grid-cols-3 border-y border-border" data-testid="stats-by-status">
              {Object.entries(contacts?.by_status || {}).map(([k, v], i) => (
                <Link
                  key={k}
                  to={`/admin/contacts?status=${k}`}
                  className={`p-6 ${
                    i !== 2 ? "border-r border-border" : ""
                  } hover:bg-muted/50 transition-colors`}
                >
                  <div className="overline text-ink/50 mb-3 capitalize">
                    {k}
                  </div>
                  <div className="display-num text-3xl md:text-4xl">{v}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
